import PropTypes from 'prop-types';
import {useEffect, useRef} from 'react';
import {useTheme} from 'react-jss';
import {connect} from 'react-redux';
import useDidUpdateEffect from '../../../hooks/useDidUpdateEffect';
import useHandleClickOutside from '../../../hooks/useHandleClickOutside';
import {hideOverlay} from '../../actions/overlay-actions';
import {isMobile} from '../../selectors/DeviceSelectors';
import {getSettings, getSiteCode} from '../../selectors/selectors';
import bigQueryMetricRecorder from '../../tracking/bigQueryMetricRecorder';
import DomUtils from '../../utils/DomUtils';
import Track from '../../utils/tracking';
import OverlayHeader from './OverlayHeader';
import useStyles from './CheckoutOverlay.styles';

export function CheckoutOverlay(props) {
  const theme = useTheme();
  const classes = useStyles({...props, theme});
  const overlayRef = useRef();
  
  useEffect(() => {
    const handleKeyUp = (event) => {
      if (event.key === 'Escape') {
        DomUtils.closeOverlayIfClosable();
      }
    };
    document.addEventListener('keyup', handleKeyUp);
    return () => document.removeEventListener('keyup', handleKeyUp);
  }, []);
  
  useHandleClickOutside(
    overlayRef,
    () => DomUtils.closeOverlayIfClosable(),
    null,
    'circularCrossIcon__clearInput',
    props.overlayName,
  );
  
  useDidUpdateEffect(() => {
    overlayRef.current.focus();
  }, [overlayRef.current]);
  
  const exit = () => {
    try {
      bigQueryMetricRecorder('CheckoutOverlay', [{
        name: 'type',
        value: 'Close button',
      }], [], 'click');
    } catch (error) {
      Track.track(error);
    }
    props.exitOverlay();
    props.exitCallback();
  };
  
  return (
    <div
      className={`${classes.checkoutOverlay} CheckoutOverlay ${props.className}`}
      aria-modal="true"
      aria-labelledby="overlayLabel__id"
      role="dialog"
      tabIndex="-1" // Role and tabIndex required for focus trap
      data-test={props['data-test']}
      ref={overlayRef}
    >
      {(props.autosize || props.popup || props.stickyScaleToContentMobile) && (
        <OverlayHeader
          useCancelText={props.useCancelText}
          title={props.title}
          translationPlaceHolders={props.translationPlaceHolders}
          sticky={props.stickyHeader}
          exitButtonCallback={exit}
          siteCode={props.siteCode}
          removeFocus={props.removeFocus}
          fullWidthOverlay={props.fullWidthOverlay}
        />
      )}
      
      <div
        className={classes.checkoutOverlayContent}
        data-test="CheckoutOverlay__content"
        id="CheckoutOverlay__content"
      >
        {props.children}
      </div>
    </div>
  );
}

const mapStateToProps = (state, ownProps) => ({
  isMobile: isMobile(state),
  siteCode: getSiteCode(state),
  useCancelText: ownProps.isCollection || getSettings(state).use_cancel_text_in_overlay,
  overlayName: state.overlay?.selectedOverlay || '',
  removeFocus: !!getSettings(state)?.should_remove_focus,
});

const mapDispatchToProps = {
  exitOverlay: hideOverlay,
};

CheckoutOverlay.propTypes = {
  // Boolean modifiers
  popup: PropTypes.bool,
  fullscreen: PropTypes.bool,
  fullscreenMobile: PropTypes.bool,
  nopadding: PropTypes.bool,
  autosize: PropTypes.bool,
  stickyHeader: PropTypes.bool,
  stickyScaleToContentMobile: PropTypes.bool,
  
  // Standard props
  title: PropTypes.string,
  siteCode: PropTypes.string,
  className: PropTypes.string,
  overlayName: PropTypes.string,
  'data-test': PropTypes.string,
  exitCallback: PropTypes.func,
  exitOverlay: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  useCancelText: PropTypes.bool.isRequired,
  removeFocus: PropTypes.bool,
  translationPlaceHolders: PropTypes.array,
  fullWidthOverlay: PropTypes.bool,
};

CheckoutOverlay.defaultProps = {
  exitCallback() {
  },
  stickyHeader: false,
  isCollection: false,
  fullWidthOverlay: false,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CheckoutOverlay);
